<script lang="ts">
import type { BadgeProps, TabsItem } from '#ui/types';

import {
    getApiAuctionsListingQueryKey,
    getApiDirectSalesListingQueryKey,
    SellingMethod,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import { type PlatformSellingMethod } from '~/types/sale.type';

/** `UTabs` emits `string | number`; this maps that back onto the enum without an unchecked cast. */
const TAB_VALUE_TO_SELLING_METHOD_MAP: Record<string, PlatformSellingMethod> = {
    [SellingMethod.Auction]: SellingMethod.Auction,
    [SellingMethod.DirectSale]: SellingMethod.DirectSale,
};

// `w-auto` overrides the theme's `w-full` so the list stays pill-sized.
// Until Reka mounts the indicator, the theme paints the active tab with a `before:` pseudo-element whose
// `rounded-md` sits behind a higher-specificity selector, so only `!` makes it round too. `[&::before]:` rather
// than `before:`, which would add `content` and turn the pseudo-element into an extra flex item once hydrated.
const TABS_UI = {
    list: 'bg-default ring-default w-auto rounded-full shadow-lg ring-1',
    indicator: 'rounded-full',
    trigger: 'rounded-full [&::before]:rounded-full!',
    leadingIcon: 'size-5',
};

// `UTabs` has no trailing icon, unlike the SLP's `UNavigationMenu`: the spinner rides in a badge stripped down to the
// icon, in the trigger's own color.
const PENDING_BADGE: BadgeProps = { icon: 'i-svg-spinners-270-ring', class: 'bg-transparent text-inherit ring-0' };
</script>

<script setup lang="ts">
// MODELS
const sellingMethod = defineModel<PlatformSellingMethod>({ required: true });

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
// The SLP's default state and nothing else: each badge matches the count its listing shows when opened from a link.
const auctionsListingParams = { state: SLP_SALE_CHANNEL_TO_DEFAULT_STATE_MAP[SellingMethod.Auction], itemsPerPage: 0 };
const directSalesListingParams = { state: SLP_SALE_CHANNEL_TO_DEFAULT_STATE_MAP[SellingMethod.DirectSale], itemsPerPage: 0 };
// Client only: neither the page nor the header modal waits for the counts, the badges spin meanwhile as in the SLP tabs.
const { data: auctionsListing, isPending: isAuctionsListingPending } = useApiAuctionsListing(auctionsListingParams, {
    query: { enabled: import.meta.client, queryKey: getApiAuctionsListingQueryKey(auctionsListingParams) },
});
const { data: directSalesListing, isPending: isDirectSalesListingPending } = useApiDirectSalesListing(directSalesListingParams, {
    query: { enabled: import.meta.client, queryKey: getApiDirectSalesListingQueryKey(directSalesListingParams) },
});
const isPending = computed<boolean>(() => isAuctionsListingPending.value || isDirectSalesListingPending.value);

const items = computed<TabsItem[]>(() => [
    {
        value: SellingMethod.DirectSale,
        label: tt('search.quick.tab.directSale'),
        icon: 'lucide:shopping-bag',
        badge: isPending.value ? PENDING_BADGE : directSalesListing.value?.totalItems,
    },
    {
        value: SellingMethod.Auction,
        label: tt('search.quick.tab.auction'),
        icon: 'lucide:gavel',
        badge: isPending.value ? PENDING_BADGE : auctionsListing.value?.totalItems,
    },
]);

// METHODS
const onUpdateSellingMethod = (value: string | number): void => {
    const nextSellingMethod = TAB_VALUE_TO_SELLING_METHOD_MAP[String(value)];

    if (nextSellingMethod) sellingMethod.value = nextSellingMethod;
};
</script>

<template>
    <UTabs
        :model-value="sellingMethod"
        :items="items"
        variant="pill"
        size="lg"
        :content="false"
        :ui="TABS_UI"
        @update:model-value="onUpdateSellingMethod"
    />
</template>
